import { useQuery } from "@apollo/client";
import {
  GetComplianceProgramTimelineQuery,
  GetComplianceProgramTimelineQueryVariables,
} from "types/graphql-schema";

import Loading from "@components/loading/loading";

import getComplianceProgramTimelineQuery from "../graphql/get-compliance-program-timeline-query";
import { useProgramsSection } from "../programs-section-context";
import { ComplianceProgramProgression } from "./compliance-program-progression";
import { conditionalTimelineMessage } from "./compliance-program-timeline-vocabulary";

/**
 * The Timeline tab of a review page.
 *
 * `ongoing` and `kickoffAfterDays` come from the page's own query rather than from
 * this one, because an ongoing review has no dates of its own and the backend dates
 * its plan from today. Without them the rows below would read as days the admin
 * chose, which they are not.
 */
const ComplianceProgramTimeline = ({
  complianceProgramId,
  ongoing,
  kickoffAfterDays,
}: {
  complianceProgramId: number;
  ongoing: boolean;
  kickoffAfterDays: number;
}) => {
  const { noun } = useProgramsSection();
  const { data, loading, error } = useQuery<
    GetComplianceProgramTimelineQuery,
    GetComplianceProgramTimelineQueryVariables
  >(getComplianceProgramTimelineQuery, {
    fetchPolicy: "cache-and-network",
    variables: { complianceProgramId },
  });

  const rows = data?.complianceProgram?.timeline ?? [];

  return (
    <div className="flex flex-col gap-4" aria-label={`${noun} timeline`}>
      <div className="text-sm text-gray-500">
        What this {noun.toLowerCase()} will do, and who each message reaches.
      </div>

      {ongoing && (
        <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {conditionalTimelineMessage(noun.toLowerCase(), kickoffAfterDays).map(
            (sentence) => (
              <p key={sentence} className="mt-1 first:mt-0">
                {sentence}
              </p>
            ),
          )}
        </div>
      )}

      <ComplianceProgramProgression
        rows={rows}
        empty={
          loading ? (
            <Loading size="5" />
          ) : /* A failed request must not read as "this does nothing". */
          error ? (
            "This timeline could not be loaded. Reload the page to try again."
          ) : (
            "Nothing is scheduled yet. Set a start date and a due date to see the timeline."
          )
        }
      />
    </div>
  );
};

export default ComplianceProgramTimeline;
